//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { useMutation } from '@tanstack/react-query';

import {
  getInspirationVideoUploadStatus,
  startInspirationVideoUpload,
  uploadCampaignRequestBrandBrief,
  uploadInspirationImage,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApi';
import { UploadCampaignRequestFilePayload } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';

const MAX_CONTENT_BRIEF_VIDEO_POLL_ATTEMPTS = 45;
const CONTENT_BRIEF_VIDEO_POLL_DELAY_MS = 2_000;
const VIDEO_PROCESSING_FAILED_ERROR =
  'Video processing failed. Please try again.';

const VIDEO_PROCESSING_UNKNOWN_ERROR =
  'Something went wrong while uploading your video. Please try again.';
interface UploadMutationPayload extends UploadCampaignRequestFilePayload {
  signal?: AbortSignal;
}

const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal === undefined) {
      setTimeout(resolve, ms);
      return;
    }

    const abortError =
      signal.reason instanceof Error
        ? signal.reason
        : new Error('Video upload polling was aborted.');

    if (signal.aborted) {
      reject(abortError);
      return;
    }

    const abortHandler = () => {
      clearTimeout(timeoutId);
      reject(abortError);
    };
    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', abortHandler);
      resolve();
    }, ms);
    signal.addEventListener('abort', abortHandler, { once: true });
  });

/**
 * Each mutation resolves to the finished asset URL.
 *
 * The brand brief upload is synchronous: the gateway stores the file in S3
 * and returns its final CloudFront URL directly. The content inspiration video
 * upload is asynchronous (PD-326135): the upload responds 202 with a
 * `videoId`, and its status is polled until processing completes.
 */
export const useUploadInspirationImageMutation = () =>
  useMutation({
    mutationFn: ({ campaignRequestId, file, signal }: UploadMutationPayload) =>
      uploadInspirationImage({ campaignRequestId, file }, { signal }),
  });

export const useUploadInspirationVideoMutation = () =>
  useMutation({
    mutationFn: async ({
      campaignRequestId,
      file,
      signal: uploadSignal,
    }: UploadMutationPayload) => {
      const { videoId } = await startInspirationVideoUpload(
        { campaignRequestId, file },
        { signal: uploadSignal },
      );

      for (
        let attempt = 0;
        attempt < MAX_CONTENT_BRIEF_VIDEO_POLL_ATTEMPTS;
        attempt += 1
      ) {
        const response = await getInspirationVideoUploadStatus(
          campaignRequestId,
          videoId,
          uploadSignal,
        );

        if (response.status === 'ready' && response.url !== null) {
          return response.url;
        }

        if (response.status !== 'processing') {
          throw new Error(VIDEO_PROCESSING_FAILED_ERROR);
        }

        if (attempt < MAX_CONTENT_BRIEF_VIDEO_POLL_ATTEMPTS - 1) {
          await delay(CONTENT_BRIEF_VIDEO_POLL_DELAY_MS, uploadSignal);
        }
      }

      throw new Error(VIDEO_PROCESSING_UNKNOWN_ERROR);
    },
  });

export const useUploadBrandBriefMutation = () =>
  useMutation({ mutationFn: uploadCampaignRequestBrandBrief });
